/**
 * useDashboardPreferences.js
 *
 * Loads, edits and persists the Manager dashboard's per-user layout.
 *
 * The layout lives on the ACCOUNT (`user.dashboardConfig.mobileManagement`) so
 * it follows the user from phone to tablet to a borrowed device, with a local
 * mirror in `persistentSettings` purely for first paint: a dashboard must not
 * flash the default layout for half a second on every cold start while the
 * network answers, and it must still render the user's own layout offline.
 *
 * Write path — optimistic, debounced, non-blocking:
 *   1. state updates immediately (the toggle animates with no latency);
 *   2. the local mirror is written right away (offline-safe);
 *   3. the account write is debounced ~700 ms and fire-and-forget — a failed
 *      save never rolls the UI back, because the local mirror is still correct
 *      and the next successful save carries the same payload.
 *
 * Read path — local mirror first, account second:
 *   the mirror paints instantly; the account copy replaces it when it arrives
 *   and differs, which is what makes a layout edited on the tablet show up on
 *   the phone. `updatedAt` breaks the tie so a pending local edit is never
 *   clobbered by a slower server read of the pre-edit layout.
 *
 * The pure rules (defaults, normalization, toggles, ordering) live in
 * `helpers/dashboardPreferences.js`; this module is only the IO around them.
 */

import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {DASHBOARD_CONFIG_NAMESPACE} from '../config/dashboardWidgets';
import {
    buildDefaultPreferences,
    moveQuickAction as moveQuickActionPure,
    moveWidget as moveWidgetPure,
    normalizeDashboardPreferences,
    serializeDashboardPreferences,
    setAllWidgets as setAllWidgetsPure,
    setKpiColumns as setKpiColumnsPure,
    toggleQuickAction as toggleQuickActionPure,
    toggleWidget as toggleWidgetPure,
} from './dashboardPreferences';
import {getSettingJSON, setSetting} from './persistentSettings';
import {getMyDashboardConfig, saveMyDashboardConfig} from '../services/userApi';

/** persistentSettings key of the local first-paint mirror. */
export const DASHBOARD_PREFERENCES_CACHE_KEY = 'MANAGEMENT_DASHBOARD_LAYOUT';

/** Debounce before the account write; long enough to batch a burst of toggles. */
const PERSIST_DEBOUNCE_MS = 700;

/**
 * Compare two `updatedAt` stamps, tolerating nulls.
 *
 * @param {?string} a First ISO timestamp.
 * @param {?string} b Second ISO timestamp.
 * @returns {boolean} True when `a` is strictly newer than `b`.
 */
const isNewer = (a, b) => {
    const left = a ? Date.parse(a) : 0;
    const right = b ? Date.parse(b) : 0;

    return Number.isFinite(left) && Number.isFinite(right) && left > right;
};

/**
 * Per-user Manager dashboard layout.
 *
 * @param {string} [profile] Dashboard profile key (`resolveDashboardProfile`) used for the defaults.
 * @returns {{
 *   preferences: import('./dashboardPreferences').DashboardPreferences,
 *   loading: boolean,
 *   customized: boolean,
 *   saving: boolean,
 *   toggleWidget: (id: string) => void,
 *   moveWidget: (id: string, direction: number, canAccess?: Function) => void,
 *   toggleQuickAction: (id: string) => void,
 *   moveQuickAction: (id: string, direction: number) => void,
 *   setKpiColumns: (columns: number) => void,
 *   setAllWidgets: (enabled: boolean, canAccess?: Function) => void,
 *   resetToDefaults: () => void,
 * }} Preference state plus the edit operations, each already persisted.
 */
export const useDashboardPreferences = (profile) => {
    const defaults = useMemo(() => buildDefaultPreferences(profile), [profile]);
    const [preferences, setPreferences] = useState(defaults);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    // `false` until we know the user actually saved a layout — the dashboard
    // uses it to offer a "personalise me" hint instead of pretending the
    // profile defaults were a deliberate choice.
    const [customized, setCustomized] = useState(false);

    const debounceRef = useRef(null);
    const latestRef = useRef(defaults);
    const mountedRef = useRef(true);

    useEffect(() => () => {
        mountedRef.current = false;

        if (debounceRef.current) {
            globalThis.clearTimeout(debounceRef.current);
        }
    }, []);

    // Re-seed when the profile resolves later than the first render (access
    // context loads async), but only while the user has no saved layout —
    // otherwise a late profile would overwrite what they chose.
    useEffect(() => {
        if (!customized && !latestRef.current?.updatedAt) {
            latestRef.current = defaults;
            setPreferences(defaults);
        }
    }, [defaults, customized]);

    useEffect(() => {
        let cancelled = false;

        const adopt = (stored, isCustom) => {
            if (cancelled || !mountedRef.current) {
                return;
            }

            const next = normalizeDashboardPreferences(stored, profile);

            latestRef.current = next;
            setPreferences(next);
            setCustomized(isCustom);
        };

        (async () => {
            // 1. Local mirror — instant paint, and the whole layout offline.
            const cached = await getSettingJSON(DASHBOARD_PREFERENCES_CACHE_KEY, null);

            if (cached && typeof cached === 'object') {
                adopt(cached, true);
            }

            if (!cancelled && mountedRef.current) {
                setLoading(false);
            }

            // 2. Account copy — cross-device truth. Fail-soft: an offline read
            //    leaves the mirror (or the defaults) in place.
            try {
                const remote = await getMyDashboardConfig(DASHBOARD_CONFIG_NAMESPACE);
                const hasRemote = remote && typeof remote === 'object' && Array.isArray(remote.widgets);

                if (hasRemote && !isNewer(latestRef.current?.updatedAt, remote.updatedAt)) {
                    adopt(remote, true);
                    await setSetting(DASHBOARD_PREFERENCES_CACHE_KEY, remote);
                }
            } catch (_error) {
                // Offline / unauthenticated: the cached or default layout stands.
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [profile]);

    /**
     * Apply a pure transform, render it immediately, mirror it locally and
     * schedule the debounced account write.
     *
     * @param {(prev: object) => object} transform Pure preference transform.
     * @returns {void}
     */
    const mutate = useCallback((transform) => {
        const next = transform(latestRef.current);

        // A no-op transform (move at the edge, quick-action cap reached) must
        // not burn a save or restamp `updatedAt`.
        if (next === latestRef.current) {
            return;
        }

        const payload = serializeDashboardPreferences(next);
        const stamped = {...next, updatedAt: payload.updatedAt};

        latestRef.current = stamped;
        setPreferences(stamped);
        setCustomized(true);
        setSaving(true);

        setSetting(DASHBOARD_PREFERENCES_CACHE_KEY, payload).catch(() => {});

        if (debounceRef.current) {
            globalThis.clearTimeout(debounceRef.current);
        }

        debounceRef.current = globalThis.setTimeout(() => {
            debounceRef.current = null;
            saveMyDashboardConfig(DASHBOARD_CONFIG_NAMESPACE, payload)
                .catch(() => {
                    // Non-fatal: the local mirror already holds the truth and the
                    // next edit re-sends the whole layout.
                })
                .finally(() => {
                    if (mountedRef.current) {
                        setSaving(false);
                    }
                });
        }, PERSIST_DEBOUNCE_MS);
    }, []);

    const toggleWidget = useCallback((id) => mutate((prev) => toggleWidgetPure(prev, id)), [mutate]);
    const moveWidget = useCallback((id, direction, canAccess) => mutate((prev) => moveWidgetPure(prev, id, direction, canAccess)), [mutate]);
    const toggleQuickAction = useCallback((id) => mutate((prev) => toggleQuickActionPure(prev, id)), [mutate]);
    const moveQuickAction = useCallback((id, direction) => mutate((prev) => moveQuickActionPure(prev, id, direction)), [mutate]);
    const setKpiColumns = useCallback((columns) => mutate((prev) => setKpiColumnsPure(prev, columns)), [mutate]);
    const setAllWidgets = useCallback((enabled, canAccess) => mutate((prev) => setAllWidgetsPure(prev, enabled, canAccess)), [mutate]);

    /**
     * Drop the personal layout and go back to this store's profile defaults —
     * on the account too, so the reset follows the user to their other devices
     * instead of silently re-syncing the old layout from the tablet.
     *
     * @returns {void}
     */
    const resetToDefaults = useCallback(() => {
        const fresh = buildDefaultPreferences(profile);

        latestRef.current = fresh;
        setPreferences(fresh);
        setCustomized(false);
        setSaving(true);

        if (debounceRef.current) {
            globalThis.clearTimeout(debounceRef.current);
            debounceRef.current = null;
        }

        setSetting(DASHBOARD_PREFERENCES_CACHE_KEY, null).catch(() => {});
        saveMyDashboardConfig(DASHBOARD_CONFIG_NAMESPACE, null)
            .catch(() => {})
            .finally(() => {
                if (mountedRef.current) {
                    setSaving(false);
                }
            });
    }, [profile]);

    return {
        preferences,
        loading,
        customized,
        saving,
        toggleWidget,
        moveWidget,
        toggleQuickAction,
        moveQuickAction,
        setKpiColumns,
        setAllWidgets,
        resetToDefaults,
    };
};

export default useDashboardPreferences;
